import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Stack,
  Avatar,
} from '@mui/material'
import { OrdersMobileView } from './OrdersMobileView'
import {
  Add,
  Search,
  FilterList,
  Refresh,
  Edit,
  Delete,
  Visibility,
  GetApp,
  PlayArrow,
  FileDownload,
  Timeline,
  ShoppingCart,
  CalendarToday,
  AttachMoney,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { MobileDataCard } from '../../components/Common/MobileDataCard'
import type { MobileDataCardAction, MobileDataCardField } from '../../components/Common/MobileDataCard'
import {
  fetchOrders,
  setFilters,
  setPagination,
  deleteOrder,
  updateOrderStatus,
} from '../../store/slices/ordersSlice'
import { ordersAPI } from '../../services/api'
import { useOrderUpdates } from '../../hooks/useWebSocket'
import { OrderDialog } from '../../components/Orders/OrderDialog'
import { OrderDetailsDialog } from '../../components/Orders/OrderDetailsDialog'
import { ConfirmDialog } from '../../components/Common/ConfirmDialog'
import OrderProgressDialog from '../../components/Orders/OrderProgressDialog'

const ORDER_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const ORDER_PRIORITIES = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

export const OrdersPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { orders, total, isLoading, filters, pagination } = useAppSelector((state) => state.orders)
  const { orders: realtimeOrders } = useOrderUpdates()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false)
  const [progressDialogOpen, setProgressDialogOpen] = useState(false)
  const [newStatus, setNewStatus] = useState('')

  // Use mobile view on mobile devices
  if (isMobile) {
    return <OrdersMobileView />
  }

  // Load orders on component mount and when filters change
  useEffect(() => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchOrders(params))
  }, [dispatch, filters, pagination])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ search: value }))
    }, 500)

    return () => clearTimeout(timeoutId)
  }

  const handleStatusFilter = (status: string) => {
    dispatch(setFilters({ status: status || undefined }))
  }

  const handlePriorityFilter = (priority: string) => {
    dispatch(setFilters({ priority: priority || undefined }))
  }

  const handleRefresh = () => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchOrders(params))
  }

  const handleCreateOrder = () => {
    setSelectedOrder(null)
    setOrderDialogOpen(true)
  }

  const handleEditOrder = (order: any) => {
    setSelectedOrder(order)
    setOrderDialogOpen(true)
  }

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order)
    setDetailsDialogOpen(true)
  }

  const handleDeleteOrder = (order: any) => {
    setSelectedOrder(order)
    setConfirmDialogOpen(true)
  }

  const handleUpdateStatus = (order: any) => {
    setSelectedOrder(order)
    setNewStatus(order.status)
    setStatusUpdateDialogOpen(true)
  }

  const handleViewProgress = (order: any) => {
    setSelectedOrder(order)
    setProgressDialogOpen(true)
  }

  const handleExportOrder = async (order: any) => {
    try {
      const blob = await ordersAPI.exportOrder(order.order_id, 'pdf')
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `order-${order.order_number}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export order:', error)
      // For now, show a simple alert since export backend isn't implemented
      alert('Export functionality will be available once backend endpoints are implemented.')
    }
  }

  const confirmStatusUpdate = async () => {
    if (selectedOrder && newStatus) {
      await dispatch(updateOrderStatus({ orderId: selectedOrder.order_id, status: newStatus }))
      setStatusUpdateDialogOpen(false)
      setSelectedOrder(null)
      setNewStatus('')
    }
  }

  const confirmDelete = async () => {
    if (selectedOrder) {
      await dispatch(deleteOrder(selectedOrder.order_id))
      setConfirmDialogOpen(false)
      setSelectedOrder(null)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'pending':
        return 'info'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'error'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'order_number',
      headerName: 'Order #',
      width: 110,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 160,
    },
    {
      field: 'product_type',
      headerName: 'Product',
      width: 120,
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 80,
      type: 'number',
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 100,
      renderCell: (params: any) => (
        <Typography variant="body2" fontWeight="medium">
          Rs. {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 90,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={getPriorityColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 100,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: (params: any) => {
        const order = params.row
        const canEdit = !['completed', 'cancelled', 'delivered'].includes(order.status?.toLowerCase())
        const canUpdateStatus = !['completed', 'cancelled', 'delivered'].includes(order.status?.toLowerCase())
        const canDelete = order.status?.toLowerCase() === 'cancelled' || !['completed', 'delivered'].includes(order.status?.toLowerCase())
        
        return [
          <GridActionsCellItem
            icon={<Visibility />}
            label="View"
            onClick={() => handleViewOrder(order)}
          />,
          <GridActionsCellItem
            icon={<Timeline />}
            label="View Progress"
            onClick={() => handleViewProgress(order)}
          />,
          <GridActionsCellItem
            icon={<Edit />}
            label={canEdit ? "Edit" : "Cannot edit completed/cancelled orders"}
            disabled={!canEdit}
            onClick={() => canEdit && handleEditOrder(order)}
          />,
          <GridActionsCellItem
            icon={<PlayArrow />}
            label={canUpdateStatus ? "Update Status" : "Cannot update status"}
            disabled={!canUpdateStatus}
            onClick={() => canUpdateStatus && handleUpdateStatus(order)}
          />,
          <GridActionsCellItem
            icon={<FileDownload />}
            label="Export"
            onClick={() => handleExportOrder(order)}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label={canDelete ? "Delete" : "Cannot delete completed/delivered orders"}
            disabled={!canDelete}
            onClick={() => canDelete && handleDeleteOrder(order)}
          />,
        ]
      },
    },
  ]

  // Render mobile card for an order
  const renderMobileOrderCard = (order: any) => {
    const canEdit = !['completed', 'cancelled', 'delivered'].includes(order.status?.toLowerCase())
    const canUpdateStatus = !['completed', 'cancelled', 'delivered'].includes(order.status?.toLowerCase())
    const canDelete = order.status?.toLowerCase() === 'cancelled' || !['completed', 'delivered'].includes(order.status?.toLowerCase())

    const fields: MobileDataCardField[] = [
      {
        label: 'Customer',
        value: order.customer_name,
        icon: <ShoppingCart sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Product',
        value: order.product_type,
      },
      {
        label: 'Quantity',
        value: order.quantity,
      },
      {
        label: 'Total Amount',
        value: `Rs. ${order.total_amount?.toLocaleString()}`,
        variant: 'currency',
      },
      {
        label: 'Status',
        value: order.status,
        variant: 'chip',
        chipColor: getStatusColor(order.status) as any,
        chipVariant: 'outlined',
      },
      {
        label: 'Priority',
        value: order.priority,
        variant: 'chip',
        chipColor: getPriorityColor(order.priority) as any,
        chipVariant: 'filled',
      },
      {
        label: 'Due Date',
        value: new Date(order.due_date).toLocaleDateString(),
        variant: 'date',
        icon: <CalendarToday sx={{ fontSize: 16 }} />,
      },
    ]

    const actions: MobileDataCardAction[] = [
      {
        label: 'View Details',
        icon: <Visibility />,
        onClick: () => handleViewOrder(order),
      },
      {
        label: 'View Progress',
        icon: <Timeline />,
        onClick: () => handleViewProgress(order),
      },
      {
        label: 'Edit Order',
        icon: <Edit />,
        onClick: () => handleEditOrder(order),
        disabled: !canEdit,
      },
      {
        label: 'Update Status',
        icon: <PlayArrow />,
        onClick: () => handleUpdateStatus(order),
        disabled: !canUpdateStatus,
      },
      {
        label: 'Export',
        icon: <FileDownload />,
        onClick: () => handleExportOrder(order),
      },
      {
        label: 'Delete',
        icon: <Delete />,
        onClick: () => handleDeleteOrder(order),
        disabled: !canDelete,
        color: 'error',
      },
    ]

    return (
      <MobileDataCard
        key={order.order_id}
        title={`Order #${order.order_number}`}
        subtitle={order.customer_name}
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <ShoppingCart />
          </Avatar>
        }
        fields={fields}
        actions={actions}
        onClick={() => handleViewOrder(order)}
      />
    )
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
            fontWeight: 600,
          }}
        >
          Orders Management
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateOrder}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Create Order
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{
        mb: 3,
        backgroundColor: 'surfaceContainer',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'outlineVariant',
        boxShadow: 1,
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{
            width: '100%',
            maxWidth: '100%',
            m: 0,
            '& .MuiGrid-item': {
              paddingLeft: { xs: '8px', sm: '12px' },
              paddingTop: { xs: '8px', sm: '12px' },
            }
          }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'surfaceContainerHighest',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'outline',
                    },
                    '&:hover fieldset': {
                      borderColor: 'onSurface',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'onSurfaceVariant' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'onSurfaceVariant' }}>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e: any) => handleStatusFilter(e.target.value)}
                  sx={{
                    backgroundColor: 'surfaceContainerHighest',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'outline',
                    },
                    '&:hover fieldset': {
                      borderColor: 'onSurface',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  }}
                >
                  {ORDER_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'onSurfaceVariant' }}>Priority</InputLabel>
                <Select
                  value={filters.priority || ''}
                  label="Priority"
                  onChange={(e: any) => handlePriorityFilter(e.target.value)}
                  sx={{
                    backgroundColor: 'surfaceContainerHighest',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'outline',
                    },
                    '&:hover fieldset': {
                      borderColor: 'onSurface',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  }}
                >
                  {ORDER_PRIORITIES.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      backgroundColor: 'surfaceVariant',
                      color: 'onSurfaceVariant',
                      minWidth: 48,
                      minHeight: 48,
                      '&:hover': {
                        backgroundColor: 'surfaceContainerHighest',
                      },
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Mobile View - Card List */}
      {isMobile ? (
        <Stack spacing={2} sx={{ mb: 10 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography color="text.secondary">Loading orders...</Typography>
            </Box>
          ) : orders && orders.length > 0 ? (
            orders.map((order: any) => renderMobileOrderCard(order))
          ) : (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No orders found</Typography>
            </Card>
          )}
        </Stack>
      ) : (
        /* Desktop/Tablet View - Data Grid */
        <Card sx={{
          backgroundColor: 'surface',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'outlineVariant',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100%',
          boxShadow: 2,
          ...(isTablet && {
            overflowX: 'auto',
            '& .MuiDataGrid-root': {
              minWidth: '1000px',
            },
          }),
        }}>
          <DataGrid
            rows={orders || []}
            columns={columns}
            getRowId={(row: any) => row.order_id}
            loading={isLoading}
            paginationMode="server"
            rowCount={total || 0}
            paginationModel={{ page: pagination.page - 1, pageSize: pagination.limit }}
            onPaginationModelChange={(model: any) => dispatch(setPagination({ page: model.page + 1, limit: model.pageSize }))}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              width: '100%',
              maxWidth: '100%',
              backgroundColor: 'surface',
              border: 'none',
              '& .MuiDataGrid-main': {
                backgroundColor: 'surface',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'surfaceContainer',
                borderBottom: '1px solid',
                borderColor: 'outlineVariant',
                color: 'onSurface',
                fontWeight: 500,
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-columnHeader': {
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'outlineVariant',
                color: 'onSurface',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                backgroundColor: 'surface',
                '&:hover': {
                  backgroundColor: 'surfaceVariant',
                },
                '&.Mui-selected': {
                  backgroundColor: 'surfaceContainer',
                  '&:hover': {
                    backgroundColor: 'surfaceContainerHighest',
                  },
                },
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: 'surfaceContainer',
                borderTop: '1px solid',
                borderColor: 'outlineVariant',
                color: 'onSurface',
              },
              '& .MuiTablePagination-root': {
                color: 'onSurface',
              },
              '& .MuiTablePagination-selectIcon': {
                color: 'onSurfaceVariant',
              },
              '& .MuiDataGrid-toolbarContainer': {
                backgroundColor: 'surfaceContainer',
                borderBottom: '1px solid',
                borderColor: 'outlineVariant',
                padding: 2,
              },
            }}
          />
        </Card>
      )}

      {/* Floating Action Button - Mobile Only */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add order"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            boxShadow: 4,
            '&:hover': {
              boxShadow: 6,
            },
          }}
          onClick={handleCreateOrder}
        >
          <Add />
        </Fab>
      )}

      {/* Dialogs */}
      <OrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        order={selectedOrder}
      />

      <OrderDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        order={selectedOrder}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Order"
        message={`Are you sure you want to delete order ${selectedOrder?.order_number}? This action cannot be undone.`}
      />

      {/* Status Update Dialog */}
      <Dialog
        open={statusUpdateDialogOpen}
        onClose={() => setStatusUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'surface',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'outlineVariant',
            boxShadow: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'surfaceContainer',
            color: 'onSurface',
            fontWeight: 500,
            fontSize: '1.5rem',
            py: 3,
            px: 4,
            borderBottom: '1px solid',
            borderColor: 'outlineVariant',
          }}
        >
          Update Order Status
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'surface', py: 4, px: 4 }}>
          <Typography variant="body2" sx={{ mb: 3, color: 'onSurfaceVariant' }}>
            Update status for order: {selectedOrder?.order_number}
          </Typography>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'onSurfaceVariant' }}>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
              sx={{
                backgroundColor: 'surfaceContainerHighest',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'outline',
                },
                '&:hover fieldset': {
                  borderColor: 'onSurface',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              }}
            >
              {ORDER_STATUSES.slice(1).map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: 'surfaceContainer',
            borderTop: '1px solid',
            borderColor: 'outlineVariant',
            p: 4,
            gap: 2,
          }}
        >
          <Button
            onClick={() => setStatusUpdateDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 7,
              borderColor: 'outline',
              color: 'onSurface',
              fontWeight: 500,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: 'surfaceVariant',
                borderColor: 'onSurface',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStatusUpdate}
            variant="contained"
            disabled={!newStatus || newStatus === selectedOrder?.status}
            sx={{
              borderRadius: 7,
              backgroundColor: 'primary.main',
              color: 'onPrimary',
              fontWeight: 500,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 4,
              },
              '&:active': {
                boxShadow: 1,
              },
              '&:disabled': {
                opacity: 0.6,
                backgroundColor: 'surfaceVariant',
                color: 'onSurfaceVariant',
                boxShadow: 'none',
              },
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Tracking Dialog */}
      <OrderProgressDialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        orderId={selectedOrder?.order_id || 0}
        orderNumber={selectedOrder?.order_number || ''}
      />
    </Box>
  )
}
